import { useCallback, useEffect, useMemo, useRef } from 'react'
import { getOptionsConfig, serializeProject } from '@rawgraphs/rawgraphs-core'
import find from 'lodash/find'
import isPlainObject from 'lodash/isPlainObject'
import baseCharts from '../charts'
import {
  deserializeLugsailProject,
  serializeLugsailProject,
} from '../services/lugsailProjectFile'
import { getDefaultChartVisualOptions } from '../services/chartSnapshot'

export default function useProjectIO({
  currentChart,
  customCharts,
  importCustomChartFromProject,
  exportCustomChart,
  hydrateFromSavedProject,
  brandTheme,
  chartColorSyncMode,
  setCurrentChart,
  setBrandTheme,
  mapping,
  setMapping,
  visualOptions,
  setVisualOptions,
  setChartColorSyncMode,
  data,
  userInput,
  userData,
  userDataType,
  parseError,
  unstackedData,
  unstackedColumns,
  separator,
  thousandsSeparator,
  decimalsSeparator,
  locale,
  stackDimension,
  dataSource,
  clearLocalMapping,
  applyLocalMapping,
  resetChartColorSync,
  setRawViz,
  pendingRestoreMappingRef,
}) {
  const getDefaultVisualOptions = useCallback(
    (chart) => getDefaultChartVisualOptions(chart),
    []
  )

  const allCharts = useMemo(
    () => baseCharts.concat(customCharts),
    [customCharts]
  )

  const importProject = useCallback(
    async (project, fromUrl) => {
      const {
        project: savedProject,
        brandTheme: savedBrandTheme,
        chartColorSyncMode,
      } = deserializeLugsailProject(project, allCharts)

      if (!savedProject?.currentChart) {
        throw new Error('Invalid project file: missing chart definition')
      }

      let nextCurrentChart
      if (savedProject.currentChart.rawCustomChart) {
        try {
          nextCurrentChart = await importCustomChartFromProject(
            savedProject.currentChart
          )
        } catch (err) {
          if (err.isAbortByUser) {
            if (fromUrl) {
              window.history.replaceState(null, null, '/')
            }
            return
          }
          throw err
        }
      } else {
        nextCurrentChart = savedProject.currentChart
      }
      // Clear stale state from any previous chart (mirrors handleChartChange)
      clearLocalMapping()
      resetChartColorSync()
      setRawViz(null)

      // Keep the mapping available as a compatibility fallback while the
      // parser works. The authoritative restoration happens after hydration
      // below, once the dataset and its inferred column types are ready.
      if (pendingRestoreMappingRef) {
        pendingRestoreMappingRef.current = savedProject.mapping
      }
      setCurrentChart(nextCurrentChart)
      const patchedOptions = { ...savedProject.visualOptions }
      Object.keys(patchedOptions).forEach((key) => {
        if (isPlainObject(patchedOptions[key])) {
          patchedOptions[key].__loaded = true
        }
      })
      if (
        Object.prototype.hasOwnProperty.call(
          getOptionsConfig(nextCurrentChart?.visualOptions),
          'showGrid'
        ) &&
        patchedOptions.showGrid === undefined
      ) {
        patchedOptions.showGrid = false
      }
      setVisualOptions(patchedOptions)
      if (savedBrandTheme) {
        setBrandTheme((currentTheme) => ({
          ...currentTheme,
          ...savedBrandTheme,
        }))
      }
      setChartColorSyncMode(
        chartColorSyncMode ||
          (savedProject.visualOptions?.colorScale ? 'manual' : 'defaults')
      )

      await hydrateFromSavedProject(savedProject)
      if (pendingRestoreMappingRef) {
        pendingRestoreMappingRef.current = null
      }
      setMapping(savedProject.mapping)
      applyLocalMapping(savedProject.mapping)
    },
    [
      allCharts,
      applyLocalMapping,
      chartColorSyncMode,
      clearLocalMapping,
      hydrateFromSavedProject,
      importCustomChartFromProject,
      pendingRestoreMappingRef,
      resetChartColorSync,
      setCurrentChart,
      setBrandTheme,
      setChartColorSyncMode,
      setMapping,
      setRawViz,
      setVisualOptions,
    ]
  )

  const lastImportProjectRef = useRef()
  useEffect(() => {
    lastImportProjectRef.current = importProject
  }, [importProject])

  useEffect(() => {
    const projectUrlStr = new URLSearchParams(window.location.search).get('url')
    let projectUrl
    try {
      projectUrl = new URL(projectUrlStr)
    } catch (e) {
      return
    }
    fetch(projectUrl)
      .then((r) => (r.ok ? r.text() : Promise.reject(r)))
      .then(
        (projectStr) => {
          const { project } = deserializeLugsailProject(projectStr, allCharts)
          const lastImportProject = lastImportProjectRef.current
          if (lastImportProject) {
            lastImportProject(project, true)
          }
        },
        (err) => {
          console.log(`Can't load ${projectUrl}`, err)
        }
      )
  }, [importProject])

  useEffect(() => {
    if (currentChart.rawCustomChart) {
      const currentCustom = find(
        customCharts,
        (chart) => chart.metadata.id === currentChart.metadata.id
      )
      if (!currentCustom) {
        setCurrentChart(baseCharts[0])
        return
      }
      if (
        currentCustom.rawCustomChart.source !==
        currentChart.rawCustomChart.source
      ) {
        setCurrentChart(currentCustom)
      }
    }
  }, [customCharts, currentChart, setCurrentChart])

  const handleChartChange = useCallback(
    (nextChart) => {
      setMapping({})
      clearLocalMapping()
      setCurrentChart(nextChart)
      setVisualOptions(getDefaultVisualOptions(nextChart))
      resetChartColorSync()
      setRawViz(null)
    },
    [
      clearLocalMapping,
      resetChartColorSync,
      setCurrentChart,
      setMapping,
      setRawViz,
      setVisualOptions,
      getDefaultVisualOptions,
    ]
  )

  const exportProject = useCallback(async () => {
    if (!data?.dataTypes || !userData) {
      return null
    }

    const customChart = await exportCustomChart(currentChart)
    return serializeProject({
      userInput,
      userData,
      userDataType,
      parseError,
      unstackedData,
      unstackedColumns,
      data,
      separator,
      thousandsSeparator,
      decimalsSeparator,
      locale,
      stackDimension,
      dataSource,
      currentChart,
      mapping,
      visualOptions,
      customChart,
    })
  }, [
    currentChart,
    data,
    dataSource,
    decimalsSeparator,
    exportCustomChart,
    locale,
    mapping,
    parseError,
    separator,
    stackDimension,
    thousandsSeparator,
    unstackedColumns,
    unstackedData,
    userData,
    userDataType,
    userInput,
    visualOptions,
  ])

  const exportLugsailProject = useCallback(async () => {
    const project = await exportProject()
    if (!project) return null

    return serializeLugsailProject({
      project,
      brandTheme,
      chartColorSyncMode,
    })
  }, [brandTheme, chartColorSyncMode, exportProject])

  return {
    importProject,
    exportProject,
    exportLugsailProject,
    handleChartChange,
  }
}
